import { Person } from "@mui/icons-material";
import { Box, Typography, Avatar, Button } from "@mui/material";
import { PatientsPaginationRequestDtoStatusEnum } from "libs/cellera-sdk";
import React, { useCallback } from "react";

import { getPatientDataSharingStatusLabel } from "../../../../helpers/getPatientDataSharingStatusLabel";
import { useCelleraTheme } from "../../../../theme/useCelleraTheme";

export interface PatientHeaderData {
  patientId: string;
  name: string;
  email?: string;
  birthday?: string;
  avatar?: string;
  phone?: string;
  status?: string;
}

interface PatientHeaderProps {
  patient: PatientHeaderData;
  onCancelRequest?: () => void;
  onRequest?: () => void;
}

const PatientHeader: React.FC<PatientHeaderProps> = ({
  patient,
  onCancelRequest,
  onRequest,
}) => {
  const { colors } = useCelleraTheme();

  const getStatusColor = useCallback(
    (status?: string) => {
      switch (status) {
        case PatientsPaginationRequestDtoStatusEnum.ActiveSharing:
          return {
            border: `1px solid ${colors.success[700]}`,
            backgroundColor: colors.success[100],
            color: colors.success[700],
          };
        case PatientsPaginationRequestDtoStatusEnum.Revoked:
          return {
            border: `1px solid ${colors.error[700]}`,
            backgroundColor: colors.error[100],
            color: colors.error[700],
          };
        case PatientsPaginationRequestDtoStatusEnum.Requesting:
          return {
            border: `1px solid ${colors.warning[700]}`,
            backgroundColor: colors.warning[100],
            color: colors.warning[700],
          };
        default:
          return {
            p: 0,
            fontSize: 14,
          };
      }
    },
    [colors]
  );

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: colors.border.main,
        bgcolor: colors.background.paper,
        borderRadius: 1,
        p: 2,
      }}
    >
      <Box className="flex items-start justify-between gap-4">
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: colors.success[700],
              color: colors.text.inverse,
              fontWeight: 700,
            }}
          >
            {patient.avatar ? (
              <img
                src={patient.avatar}
                alt={patient.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Person sx={{ fontSize: 30 }} />
            )}
          </Avatar>

          <Box className="flex-1">
            <Typography
              variant="h4"
              sx={{ color: colors.text.primary, fontWeight: 700, mb: 0.5 }}
            >
              {patient.name}
            </Typography>
            <Box className="flex flex-col gap-1">
              <Typography
                variant="body2"
                sx={{ color: colors.text.secondary, fontWeight: 600 }}
              >
                <span>Email:</span> {patient.email ?? "N/A"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.text.secondary, fontWeight: 600 }}
              >
                <span>Birthday:</span> {patient.birthday ?? "N/A"}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className="flex flex-col gap-1.5 items-start">
          <Typography
            variant="body2"
            sx={{ color: colors.text.secondary, fontWeight: 600 }}
          >
            <span>Phone:</span> {patient.phone ?? "N/A"}
          </Typography>
          <Box className="flex items-center gap-2">
            <Typography
              variant="body2"
              sx={{ color: colors.text.secondary, fontWeight: 600 }}
            >
              <span>Status:</span>
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1.5,
                py: 0.5,
                borderRadius: 0.5,
                fontSize: 12,
                fontWeight: 600,
                ...getStatusColor(patient.status),
              }}
            >
              {getPatientDataSharingStatusLabel(patient.status)}
            </Box>
          </Box>
        </Box>

        {patient.status === PatientsPaginationRequestDtoStatusEnum.Requesting &&
          onCancelRequest && (
            <Box>
              <Button
                variant="outlined"
                size="small"
                onClick={onCancelRequest}
                sx={{
                  textTransform: "none",
                  borderColor: colors.error[700],
                  color: colors.error[700],
                  fontWeight: 600,
                  borderRadius: "100px",
                  alignItems: "center",
                  display: "flex",
                  mt: 0.5,
                  "&:hover": {
                    borderColor: colors.error[700],
                    backgroundColor: colors.error[50],
                  },
                }}
              >
                Cancel Request
              </Button>
            </Box>
          )}

        {patient.status === PatientsPaginationRequestDtoStatusEnum.Revoked &&
          onRequest && (
            <Box>
              <Button
                variant="contained"
                size="small"
                onClick={onRequest}
                sx={{
                  textTransform: "none",
                  backgroundColor: colors.primary.main,
                  color: colors.primary.contrast,
                  fontWeight: 600,
                  borderRadius: "100px",
                  alignItems: "center",
                  display: "flex",
                  mt: 0.5,
                  "&:hover": {
                    backgroundColor: colors.primary.dark,
                  },
                }}
              >
                Request
              </Button>
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default PatientHeader;
